import type { CSSProperties } from "react";
import { isColorOption, isSizeOption, sortOptionNames, sortValues } from "../lib/format.ts";

interface VariantLike {
  id: string;
  options: Record<string, string>;
  available?: boolean;
  swatch?: string | null;
}

/** Variant chooser: radio groups per option, impossible combinations disabled. */
export function OptionPicker<V extends VariantLike>({
  variants,
  selected,
  onSelect,
}: {
  variants: V[];
  selected: Record<string, string>;
  onSelect: (next: Record<string, string>) => void;
}) {
  const names = sortOptionNames([...new Set(variants.flatMap((v) => Object.keys(v.options)))]);
  return (
    <div className="optionpicker">
      {names.map((name) => {
        const values = sortValues(name, [
          ...new Set(variants.map((v) => v.options[name]).filter(Boolean) as string[]),
        ]);
        const color = isColorOption(name);
        return (
          <fieldset key={name} className="optionpicker__group">
            <legend className="field-label">
              {name}
              {selected[name] && <span className="optionpicker__value">{selected[name]}</span>}
            </legend>
            <div className="chips" role="radiogroup" aria-label={name}>
              {values.map((val) => {
                const possible = variants.some(
                  (v) =>
                    v.available !== false &&
                    v.options[name] === val &&
                    names.every((n) => n === name || !selected[n] || v.options[n] === selected[n]),
                );
                const swatch = color ? variants.find((v) => v.options[name] === val)?.swatch : null;
                const checked = selected[name] === val;
                const pick = () => {
                  const next = { ...selected, [name]: val };
                  // Drop choices that no longer combine with the new one.
                  for (const n of names) {
                    if (
                      n !== name &&
                      next[n] &&
                      !variants.some((v) => v.options[name] === val && v.options[n] === next[n])
                    ) {
                      delete next[n];
                    }
                  }
                  onSelect(next);
                };
                return swatch ? (
                  <button
                    key={val}
                    type="button"
                    role="radio"
                    aria-checked={checked}
                    aria-label={val}
                    title={val}
                    className="swatch"
                    style={{ "--swatch": swatch } as CSSProperties}
                    disabled={!possible}
                    onClick={pick}
                  />
                ) : (
                  <button
                    key={val}
                    type="button"
                    role="radio"
                    aria-checked={checked}
                    className="chip"
                    disabled={!possible}
                    onClick={pick}
                  >
                    {val}
                  </button>
                );
              })}
            </div>
          </fieldset>
        );
      })}
    </div>
  );
}

export function findVariant<V extends VariantLike>(
  variants: V[],
  selected: Record<string, string>,
): V | undefined {
  const names = [...new Set(variants.flatMap((v) => Object.keys(v.options)))];
  return variants.find(
    (v) => v.available !== false && names.every((n) => v.options[n] === selected[n]),
  );
}

/** Preselect the first available variant's options (except size, which people should choose). */
export function initialSelection<V extends VariantLike>(variants: V[]): Record<string, string> {
  const first = variants.find((v) => v.available !== false);
  if (!first) return {};
  return Object.fromEntries(Object.entries(first.options).filter(([k]) => !isSizeOption(k)));
}

export function Quantity({
  value,
  onChange,
  label,
}: {
  value: number;
  onChange: (n: number) => void;
  label: string;
}) {
  return (
    <div className="qty" role="group" aria-label={label}>
      <button
        type="button"
        onClick={() => onChange(Math.max(1, value - 1))}
        disabled={value <= 1}
        aria-label="One less"
      >
        −
      </button>
      <output className="tabular" aria-live="polite">
        {value}
      </output>
      <button
        type="button"
        onClick={() => onChange(Math.min(20, value + 1))}
        disabled={value >= 20}
        aria-label="One more"
      >
        +
      </button>
    </div>
  );
}
